import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Page,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Frame,
  Stack
} from '@shopify/polaris';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import LandingNav from '../landing/components/LandingNav';
import { SystemSettingsService } from '../../services/systemSettingsService';
import { BetaInviteService } from '../../services/betaInviteService';

function Register() {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [betaCode, setBetaCode] = useState('');
  const [referralBonusCredits, setReferralBonusCredits] = useState(500); // Default value
  const [signUpRestricted, setSignUpRestricted] = useState(false);
  const [betaModeEnabled, setBetaModeEnabled] = useState(false);
  
  const navigate = useNavigate();
  const { signup } = useAuth();

  // Extract referral code from URL if present and fetch bonus settings
  useEffect(() => {
    // Extract referral code from URL if present
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
      console.log('Referral code found:', ref);
    }
    
    // Extract beta code from URL if present
    const beta = params.get('beta');
    if (beta) {
      setBetaCode(beta);
      console.log('Beta code found:', beta);
    }
    
    // Check if regular sign-ups are disabled and if beta mode is enabled
    const checkSignUpRestrictions = async () => {
      try {
        const restrictions = await SystemSettingsService.getPublicSignUpRestrictions();
        setSignUpRestricted(restrictions.disableRegularSignUp);
        
        // Check beta mode status
        const betaStatus = await BetaInviteService.getPublicBetaStatus();
        setBetaModeEnabled(betaStatus.betaModeEnabled);
      } catch (error) {
        console.error('Error checking sign-up restrictions:', error);
      }
    };
    
    checkSignUpRestrictions();
    
    // Fetch referral bonus settings
    const fetchReferralBonusSettings = async () => {
      try {
        const settingsRef = doc(db, 'system_settings', 'referral_settings');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          console.log('Referral settings data:', settingsData);
          
          if (settingsData.bonusCredits) {
            const bonusCredits = parseInt(settingsData.bonusCredits);
            if (!isNaN(bonusCredits)) {
              setReferralBonusCredits(bonusCredits);
              console.log('Referral bonus credits from settings:', bonusCredits);
            } else {
              console.warn('Invalid bonus credits value:', settingsData.bonusCredits);
              setReferralBonusCredits(500);
            }
          } else {
            console.warn('No bonusCredits field in settings');
            setReferralBonusCredits(500);
          }
        } else {
          // Use default value if settings don't exist
          console.warn('No referral_settings document exists');
          setReferralBonusCredits(500);
        }
      } catch (error) {
        console.error('Error fetching referral bonus settings:', error);
        // Use default value if there's an error
        setReferralBonusCredits(500);
      }
    };
    
    fetchReferralBonusSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!fullName.trim()) {
      setError('Full name is required');
      return;
    }
    
    // Check if beta code is required but not provided
    if (betaModeEnabled && !betaCode.trim()) {
      setError('Beta code is required for registration');
      return;
    }

    try {
      setLoading(true);
      const success = await signup(email, password, fullName, referralCode, betaCode);
      if (success) {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFullName('');
        setBetaCode('');
        setError('');
        setSuccess('Registration successful! Please check your email for verification.');
        // Don't navigate away - let the user see the success message
      }
    } catch (err) {
      console.error('Register error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/register" onNavClick={(path) => navigate(path)} />
        
        <div style={{
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e0f7fa 100%)',
          minHeight: 'calc(100vh - 120px)',
          padding: '40px 0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            padding: '0 20px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{ width: '100%', maxWidth: '500px' }}>
              <Card>
                {/* Show beta banner if beta mode is enabled, otherwise show referral restriction banner if needed */}
                {betaModeEnabled ? (
                  <Banner status="info" title="Beta Program">
                    <p>This application is currently in beta. You need a valid beta code to register.</p>
                  </Banner>
                ) : (signUpRestricted && !referralCode) && (
                  <Banner status="critical" title="Registration Restricted">
                    <p>Regular sign-ups are currently disabled. Please use a valid affiliate link to register.</p>
                  </Banner>
                )}
                
                <Card.Section>
                  <Text variant="headingLg" as="h2" alignment="center">
                    Create Your Account
                  </Text>
                </Card.Section>

                {error && (
                  <Card.Section>
                    <Banner status="critical" onDismiss={() => setError('')}>
                      <p>{error}</p>
                    </Banner>
                  </Card.Section>
                )}
                
                {success && (
                  <Card.Section>
                    <Banner status="success" onDismiss={() => setSuccess('')}>
                      <p>{success}</p>
                    </Banner>
                  </Card.Section>
                )}
                
                {referralCode && (
                  <Card.Section>
                    <Banner status="success" title="Referral Bonus">
                      <p>You've been referred by a partner! You'll receive <strong>{referralBonusCredits} Flex Credits</strong> (our premium currency) when you register, which will never expire for your account.</p>
                    </Banner>
                  </Card.Section>
                )}

                <Card.Section>
                  <form onSubmit={handleSubmit}>
                    <FormLayout>
                      <TextField
                        type="email"
                        label="Email"
                        value={email}
                        onChange={setEmail}
                        autoComplete="email"
                        required
                        disabled={loading}
                      />
                      <TextField
                        type="text"
                        label="Full Name"
                        value={fullName}
                        onChange={setFullName}
                        autoComplete="name"
                        required
                        disabled={loading}
                      />
                      {/* Referral code is hidden since it's handled via URL parameters */}
                      {referralCode && (
                        <div style={{ marginBottom: '16px' }}>
                          <Text variant="bodyMd" as="p" color="success">
                            Referral code applied: You'll receive {referralBonusCredits} bonus flex credits!
                          </Text>
                        </div>
                      )}
                      
                      {betaModeEnabled && (
                        <TextField
                          label="Beta Code"
                          value={betaCode}
                          onChange={setBetaCode}
                          autoComplete="off"
                          required={betaModeEnabled}
                          disabled={loading}
                          helpText="Enter your beta invitation code"
                        />
                      )}
                      
                      <TextField
                        type="password"
                        label="Password"
                        value={password}
                        onChange={setPassword}
                        autoComplete="new-password"
                        required
                        disabled={loading}
                      />
                      <TextField
                        type="password"
                        label="Confirm Password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        autoComplete="new-password"
                        error={password !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : undefined}
                        required
                        disabled={loading}
                      />
                      <Button
                        submit
                        primary
                        fullWidth
                        loading={loading}
                        disabled={loading}
                      >
                        Register
                      </Button>
                    </FormLayout>
                  </form>
                </Card.Section>

                <Card.Section>
                  <Stack vertical spacing="extraTight">
                    <Text variant="bodyMd" as="p" alignment="center">
                      Already have an account?{' '}
                      <a href="/auth" style={{ textDecoration: 'underline', cursor: 'pointer', color: '#008060' }}>Login</a>
                    </Text>
                  </Stack>
                </Card.Section>
              </Card>
              
              {/* Benefits Section */}
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  flexWrap: 'wrap', 
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <div style={{ 
                    background: 'white', 
                    padding: '10px 15px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>🔄</span>
                    <span>Cancel Anytime</span>
                  </div>
                  <div style={{ 
                    background: 'white', 
                    padding: '10px 15px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>🔧️</span>
                    <span>Powerful Tools</span>
                  </div>
                  <div style={{ 
                    background: 'white', 
                    padding: '10px 15px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>🤖</span>
                    <span>AI Templates</span>
                  </div>
                </div>
                
                <Text variant="bodySm" color="subdued">
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    <span>🔒 Secure Registration</span>
                    <span>|</span>
                    <span>💳 Flexible Plans</span>
                    <span>|</span>
                    <span>📱 Mobile Friendly</span>
                  </span>
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </Frame>
  );
}

export default Register;
